/**
 * Run writes to ONE document one at a time.
 *
 * Frappe stamps every document with `modified` and rejects a write whose read predates the current
 * value: `"… has been modified after you have opened it"`. Two writes fired together against the
 * same document therefore cannot both succeed — the first bumps the timestamp and the second is
 * refused, however unrelated their fields are.
 *
 * That is not a hypothetical. Applying a markup to every line fired one PATCH per line without
 * awaiting, and on a twenty-line quotation one line was repriced and nineteen threw, four
 * milliseconds apart. See this module's test.
 *
 * `useEnquiryMutations`'s `withEnquiryLock` solves the same shape for enquiries by chaining
 * promises per enquiry id. This is the caller-side equivalent, with nothing enquiry-specific in it.
 */

export interface SerialResult {
  done: number
  failed: number
  /** The first failure, normalised to an `Error` so a caller can always read `.message`. */
  firstError: Error | null
}

/**
 * Await each task before starting the next, and STOP at the first failure.
 *
 * Stopping is deliberate. These are writes to one document: once one has failed, the state the
 * remaining tasks were computed against is no longer certain, and continuing would pile further
 * failures onto a document already in an unknown state. Better to report what landed and let the
 * caller re-read.
 */
export async function runSerially(tasks: ReadonlyArray<() => Promise<unknown>>): Promise<SerialResult> {
  let done = 0

  for (const task of tasks) {
    try {
      await task()
      done += 1
    } catch (error) {
      return {
        done,
        failed: 1,
        firstError: error instanceof Error ? error : new Error(String(error)),
      }
    }
  }

  return { done, failed: 0, firstError: null }
}


/* ==============================================================================================
 * A write queue per document
 *
 * `runSerially` orders a list of writes the caller already has in hand. It cannot order writes
 * that come from DIFFERENT places — and on the quotation screen they do: the commission panel
 * PUTs terms, the lines PATCH themselves, the taxes grid replaces its table, and a commission
 * field commits on blur at the moment the Apply button is clicked.
 *
 * Any two of those landing together fail the same way:
 *
 *     QTN-2026-00011 has been modified after you have opened it (…33.509627, …33.513194)
 *
 * Serialising inside one action was not enough, because the collision is BETWEEN actions. This
 * queue is keyed on the document, so every write to one document waits for the previous one
 * whatever part of the screen it came from.
 *
 * Mirrors `useEnquiryMutations`'s `withEnquiryLock`, which solves exactly this for enquiries;
 * kept here rather than imported so the quotation path owes nothing to the enquiry path.
 * ============================================================================================ */

const documentLocks = new Map<string, Promise<unknown>>()

/**
 * Run `fn` after every write already queued for `documentId`.
 *
 * A failed link does not wedge the queue: the chain the NEXT caller waits on swallows the
 * rejection, while the promise returned to THIS caller still rejects so the error is reported.
 */
export function withDocumentLock<T>(documentId: string, fn: () => Promise<T>): Promise<T> {
  const prior = documentLocks.get(documentId) ?? Promise.resolve()
  const next = prior.then(fn, fn)
  documentLocks.set(
    documentId,
    next.then(
      () => undefined,
      () => undefined,
    ),
  )
  return next
}
